import { useState } from "react";
import { Send, Eye, Loader2, Mail, Users } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

/**
 * Compose and send a broadcast newsletter to every non-unsubscribed
 * subscriber via Resend. Includes:
 *  - Subject + HTML body (paragraphs auto-wrap; you can also paste HTML)
 *  - Dry-run count check so you know how many people will receive it
 *  - Confirmation before sending
 *  - Live preview toggle
 */
export default function AdminNewsletter() {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [preview, setPreview] = useState(false);
  const [count, setCount] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const bodyAsHtml = (raw) => {
    // If the user pasted actual HTML, respect it. Otherwise, wrap each
    // non-empty line in a paragraph so paragraph breaks work naturally.
    if (/<[a-z][\s\S]*>/i.test(raw)) return raw;
    return raw
      .split(/\n{2,}/)
      .map((p) => `<p style="margin:0 0 14px 0;line-height:1.6;">${p.replace(/\n/g, "<br>")}</p>`)
      .join("\n");
  };

  const runDryRun = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/newsletter/send", {
        subject: subject || "(no subject)",
        body_html: bodyAsHtml(body || " "),
        dry_run: true,
      });
      setCount(data.would_send_to);
      toast.success(`Would send to ${data.would_send_to} subscriber${data.would_send_to === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't check subscribers.");
    } finally { setBusy(false); }
  };

  const send = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error("Add a subject and a body first.");
      return;
    }
    const target = count ?? "all";
    if (!window.confirm(`Send "${subject}" to ${target === "all" ? "everyone" : `${target} subscribers`}?\n\nThis cannot be undone.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/newsletter/send", {
        subject: subject.trim(),
        body_html: bodyAsHtml(body),
      });
      setResult(data);
      toast.success(`Sent to ${data.sent} of ${data.recipients} — ${data.failed} failed.`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't send newsletter.");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-4xl" data-testid="admin-newsletter-page">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-fredoka text-3xl" style={{ color: "#2D3055" }}>Newsletter Composer</h1>
          <p className="font-nunito text-sm mt-1" style={{ color: "#7E82A1" }}>
            Send a warm little update to your readers. Delivered by Resend.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setPreview((v) => !v)}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full font-fredoka text-xs"
          style={{ background: "#FFFFFF", color: "#2D3055", border: "1.5px solid #E4DFF6" }}
          data-testid="admin-newsletter-preview-toggle"
        >
          <Eye size={12} /> {preview ? "Hide preview" : "Show preview"}
        </button>
      </div>

      <div className="paper-card p-5 space-y-4">
        <div>
          <label className="block font-fredoka text-sm mb-1" style={{ color: "#2D3055" }}>Subject</label>
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            maxLength={200}
            placeholder="A little update from M.K. ✨"
            className="w-full px-4 py-3 rounded-2xl border outline-none font-nunito text-sm"
            style={{ borderColor: "#F0EBFF", background: "#FFFFFF", color: "#2D3055" }}
            data-testid="admin-newsletter-subject"
          />
        </div>
        <div>
          <label className="block font-fredoka text-sm mb-1" style={{ color: "#2D3055" }}>Body</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={12}
            placeholder="Write like you're writing to a friend. Blank lines start new paragraphs. You can also paste HTML."
            className="w-full px-4 py-3 rounded-2xl border outline-none font-nunito text-sm resize-y"
            style={{ borderColor: "#F0EBFF", background: "#FFFFFF", color: "#2D3055" }}
            data-testid="admin-newsletter-body"
          />
        </div>

        {preview && (
          <div className="rounded-2xl p-5" style={{ background: "#FFF3F8", border: "1px solid #F0EBFF" }}>
            <p className="font-caveat text-xl mb-2" style={{ color: "#FF6B9D" }}>preview</p>
            <p className="font-fredoka text-lg mb-3" style={{ color: "#2D3055" }}>
              {subject || <span style={{ color: "#7E82A1" }}>(no subject)</span>}
            </p>
            <div className="prose prose-sm" style={{ color: "#3D405B" }}
                 dangerouslySetInnerHTML={{ __html: bodyAsHtml(body || "<p style='color:#7E82A1'>Type a body to see it here…</p>") }} />
          </div>
        )}

        <div className="flex items-center gap-3 pt-2 flex-wrap">
          <button
            type="button"
            onClick={runDryRun}
            disabled={busy}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full font-fredoka text-sm"
            style={{ background: "#FFFFFF", color: "#2D3055", border: "1.5px solid #E4DFF6" }}
            data-testid="admin-newsletter-dryrun"
          >
            <Users size={14} /> Count subscribers
          </button>
          {count != null && (
            <span className="font-caveat text-xl" style={{ color: "#7E82A1" }}>
              → {count} people will receive it
            </span>
          )}
          <button
            type="button"
            onClick={send}
            disabled={busy || !subject.trim() || !body.trim()}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-fredoka text-sm text-white disabled:opacity-60"
            style={{
              background: "linear-gradient(135deg,#FF6B9D 0%,#FF8C42 100%)",
              boxShadow: "0 10px 24px rgba(255,140,66,0.28)",
            }}
            data-testid="admin-newsletter-send"
          >
            {busy ? (<><Loader2 size={14} className="animate-spin" /> Sending…</>) : (<><Send size={14} /> Send now</>)}
          </button>
        </div>

        {result && (
          <div className="rounded-2xl p-4 flex items-start gap-3"
               style={{ background: "#F3FAF6", border: "1px solid #A9E8C4" }}
               data-testid="admin-newsletter-result">
            <Mail size={20} style={{ color: "#3FB07B" }} />
            <div>
              <p className="font-fredoka" style={{ color: "#2D3055" }}>
                Sent to {result.sent} of {result.recipients} subscribers.
              </p>
              {result.failed > 0 && (
                <p className="text-sm font-nunito mt-1" style={{ color: "#D6407E" }}>
                  {result.failed} email{result.failed === 1 ? "" : "s"} failed — check the logs for details.
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
